import type {
  Shape,
  ShapeStyle,
  ShapeText,
} from "../../adapters/types";
import { generateId } from "../../core/generateId";
import {
  extractRawId,
  lngLatToMercator,
  mercatorToLngLat,
  parseLayeredDocument,
  type DefaultShapeStyles,
  type JsonFormatConnector,
  type LoadedData,
  type ParseJsonFeatureOptions,
  type ParseJsonOptions,
  type RawConfigurations,
  type RawDocument,
} from "./shared";

/** Format v1 (voir BESOINS.md §8.2) : historique, gelé tel quel — `formatVersion` absent ou `1`. */

/**
 * `properties.entity` reste opaque (voir `Shape.entity`), mais certaines de ses variantes
 * (`{ type: "Place", entity: { image, ... } }`) portent une image à plaquer sur la forme, au même
 * titre que `properties.extra.imageSrc`. On la lit ici sans typer/nommer le concept "Place" —
 * seule sa présence structurelle (`entity.entity.image`, une chaîne) nous intéresse. Retiré en v2
 * (voir `v2.ts`) : plus aucune image dérivée d'`entity`. Exportée pour que `v2.ts`'s
 * `migrateV1DocumentToV2` réutilise exactement cette même lecture plutôt que d'en dupliquer une copie.
 */
export function extractEntityImageSrc(entity: unknown): string | undefined {
  if (typeof entity !== "object" || entity === null) return undefined;
  const inner = (entity as Record<string, unknown>).entity;
  if (typeof inner !== "object" || inner === null) return undefined;
  const image = (inner as Record<string, unknown>).image;
  return typeof image === "string" ? image : undefined;
}

/** Forme (v1) de `extra.icon` — mêmes champs que `ShapeImage`, mais jamais `svg`/`isDirect` (v1
 * porte ces deux notions séparément, voir `RawFeatureProperties.extra`/`resolveV1Image`). */
interface RawIcon {
  src: string;
  width?: number;
  height?: number;
  scale?: number;
  rotation?: number;
}

export interface RawFeatureProperties {
  id?: number | string;
  /** Voir `Shape.shapeKind` : `"rectangle"` distingue un rectangle d'un polygone libre — toute
   * autre valeur (ou absence) est traitée comme un polygone libre. */
  type?: string;
  extra?: {
    lock?: boolean;
    style?: ShapeStyle;
    /** Voir `Shape.hoverStyle`. Absent : la forme retombe sur le survol historique fixe de
     * l'adapter (non configurable). */
    styleHover?: ShapeStyle;
    /** Voir `Shape.action`. */
    action?: string;
    /** Voir `Shape.useActionIcon`. */
    useActionIcon?: boolean;
    imageSrc?: string;
    /** Voir `Shape.image.svg` : certaines URLs de CDN (sans extension) servent aussi bien du
     * SVG que du raster — ce champ tranche explicitement quand la donnée le sait. Indépendant de la
     * source gagnante (`resolveV1Image`) : s'applique à `extra.imageSrc` comme à `extra.icon`. */
    imageSvg?: boolean;
    /** Voir `Shape.text`/`ShapeText`. */
    text?: ShapeText;
    /** Voir `Shape.hoverText`. */
    textHover?: ShapeText;
    /** Voir `Shape.image`/`ShapeImage`. Renommé `image` (et fusionné avec `imageSrc`) en v2 — voir `v2.ts`. */
    icon?: RawIcon;
  };
  entity?: unknown;
}

/** Source gagnante résolue par `resolveV1Image` — mêmes champs que `ShapeImage` moins `svg`
 * (indépendant de la priorité, voir `RawFeatureProperties.extra.imageSvg`) et `isDirect` (porté
 * séparément par le champ `isDirect` ci-dessous, plutôt que déduit après coup par l'appelant). */
export interface ResolvedV1Image {
  src: string;
  width?: number;
  height?: number;
  scale?: number;
  rotation?: number;
  isDirect: boolean;
}

/**
 * Résout la source d'image gagnante d'un feature v1 — priorité `extra.imageSrc` direct >
 * `extra.icon.src` > image déduite d'`entity` (voir `extractEntityImageSrc`) : `extra.icon` est
 * lui-même une config explicite de la forme, tout aussi "directe" que `extra.imageSrc`, donc plus
 * légitime qu'une simple retombée d'une charge par ailleurs opaque. Réutilisée par
 * `parseJsonFeature` (produit `Shape.image` directement) ET par
 * `../migrations/v1ToV2.ts`'s `migrateV1DocumentToV2` (produit `extra.image` du JSON v2) — même
 * logique de priorité, deux formes de sortie différentes construites par chaque appelant.
 *
 * L'extraction depuis `entity` est toujours tentée (plus d'option pour la désactiver au
 * chargement) : filtrer l'affichage des images ainsi déduites (`isDirect: false`) se fait
 * maintenant au rendu, via `TokaMapConfig.includeIndirectImages`/
 * `MapLibreAdapterOptions.includeIndirectImages`, pas au chargement.
 */
export function resolveV1Image(properties: RawFeatureProperties): ResolvedV1Image | undefined {
  const directImageSrc = properties.extra?.imageSrc;
  if (directImageSrc != null) return { src: directImageSrc, isDirect: true };

  // `src` vide (vu dans exemple.json — voir CLAUDE.md) : traité comme "pas d'icône", même
  // convention que les autres champs optionnels absents.
  const rawIcon = properties.extra?.icon;
  if (rawIcon?.src) {
    return {
      src: rawIcon.src,
      width: rawIcon.width,
      height: rawIcon.height,
      scale: rawIcon.scale,
      rotation: rawIcon.rotation,
      isDirect: false,
    };
  }

  const entityImageSrc = extractEntityImageSrc(properties.entity);
  if (entityImageSrc != null) return { src: entityImageSrc, isDirect: false };

  return undefined;
}

/** Le format d'un "feature" du fichier exemple.json v1 — voir BESOINS.md §8. Exporté (au lieu de
 * rester privé à ce module) pour que `createTokaMap`'s commande console `addShapeFromFeature`
 * (voir `createTokaMap.ts`) puisse accepter directement une donnée au même format, copiée depuis
 * le fichier réel, plutôt que de réinventer un format d'entrée. */
export interface RawFeature {
  type: "Feature";
  geometry: {
    type: string;
    coordinates: [number, number][][];
    properties?: RawFeatureProperties;
  };
}

/**
 * Convertit un unique "feature" du format exemple.json v1 (coordonnées Web Mercator, `properties`
 * imbriqué dans `geometry` — voir `RawFeature`) en `Shape` (WGS84, `style` à la racine). Fonction
 * pure, extraite de `parseDocument` pour être réutilisable sur une forme isolée (voir
 * `createTokaMap`'s `addShapeFromFeature`), pas seulement au sein d'un document complet.
 *
 * `null` si `feature.geometry.type` n'est pas "Polygon", ou si l'anneau a moins de 3 points —
 * mêmes critères de validité que `parseDocument` (features silencieusement ignorées).
 */
export function parseJsonFeature(
  feature: RawFeature,
  options: ParseJsonFeatureOptions = {},
): Shape | null {
  if (feature.geometry?.type !== "Polygon") return null;
  const ring = feature.geometry.coordinates[0];
  if (!ring || ring.length < 3) return null;

  const properties = feature.geometry.properties ?? {};
  const id = properties.id ?? generateId();
  const resolvedImage = resolveV1Image(properties);

  return {
    id: String(id),
    type: "polygon",
    shapeKind: properties.type === "rectangle" ? "rectangle" : "polygon",
    coordinates: ring.map(mercatorToLngLat),
    rotation: 0,
    style: properties.extra?.style,
    hoverStyle: properties.extra?.styleHover,
    action: properties.extra?.action,
    useActionIcon: properties.extra?.useActionIcon,
    text: properties.extra?.text,
    hoverText: properties.extra?.textHover,
    locked: properties.extra?.lock,
    image: resolvedImage ? { ...resolvedImage, svg: properties.extra?.imageSvg } : undefined,
    layer: options.layer,
    order: options.order,
    entity: properties.entity,
  };
}

/**
 * Sens inverse de `parseJsonFeature` : reconstruit un `RawFeature` (coordonnées Web
 * Mercator, `properties` imbriqué dans `geometry`, `properties.type`, `extra.style`/
 * `extra.styleHover`/`extra.action`/`extra.text`/`extra.textHover`/`extra.lock`/`extra.imageSrc`/
 * `extra.imageSvg`/`extra.icon`) à partir d'un
 * `Shape` — pratique pour retrouver le format d'origine dans `TokaMapConfig.onSave` (ex. avant
 * d'envoyer la forme à une API qui attend ce format), sans avoir à dupliquer la logique de
 * conversion à la main.
 *
 * Fonction pure, pas d'arrondi/perte volontaire au-delà de la reprojection elle-même (WGS84 ->
 * Web Mercator, l'inverse exact de celle appliquée au chargement) et de la reconversion de l'id en
 * `number` (voir `extractRawId`).
 *
 * Pertes connues, inhérentes à `parseJsonFeature` (de l'info absente de `Shape`, donc
 * irrécupérable ici) :
 * - `Shape.layer`/`Shape.order` n'ont pas d'équivalent sur un `RawFeature` isolé : dans
 *   `exemple.json`, le calque est porté par la clé du dictionnaire `layers` qui CONTIENT le
 *   feature, pas par le feature lui-même — rien à écrire ici.
 * - `Shape.image` : réécrit dans `extra.imageSrc` (juste `src`) SI `shape.shapeKind === "image"` —
 *   v1 n'a jamais eu de `width`/`height`/`scale`/`rotation` sur une image directe, ces champs sont
 *   donc silencieusement perdus dans ce cas (perte acceptée, comme les autres listées ici). Sinon, réécrit
 *   dans `extra.icon` (`src`/`width`/`height`/`scale`/`rotation`) — y compris si l'image vient
 *   d'`entity` (voir `extractEntityImageSrc`) : la réécrire en `icon` plutôt qu'en `imageSrc` est
 *   incorrecte dans l'absolu (elle apparaîtrait comme explicitement configurée au prochain
 *   chargement), mais `Shape` ne garde plus trace de cette distinction fine une fois résolue — perte
 *   acceptée, comme les autres.
 */
export function shapeToRawFeature(shape: Shape): RawFeature {
  const image = shape.image;
  return {
    type: "Feature",
    geometry: {
      type: "Polygon",
      coordinates: [shape.coordinates.map(lngLatToMercator)],
      properties: {
        id: extractRawId(shape),
        // Vu uniquement pour les rectangles dans exemple.json — un polygone libre n'a pas ce champ
        // du tout (pas de `"type": "polygon"` explicite), plutôt que de l'ajouter à toutes les
        // formes reconstruites.
        ...(shape.shapeKind === "rectangle" ? { type: "rectangle" } : {}),
        extra: {
          ...(shape.locked !== undefined ? { lock: shape.locked } : {}),
          ...(shape.style !== undefined ? { style: shape.style } : {}),
          ...(shape.hoverStyle !== undefined ? { styleHover: shape.hoverStyle } : {}),
          ...(shape.action !== undefined ? { action: shape.action } : {}),
          ...(shape.useActionIcon !== undefined ? { useActionIcon: shape.useActionIcon } : {}),
          ...(image && shape.shapeKind === "image" ? { imageSrc: image.src } : {}),
          ...(image && shape.shapeKind !== "image"
            ? { icon: { src: image.src, width: image.width, height: image.height, scale: image.scale, rotation: image.rotation } }
            : {}),
          ...(image?.svg !== undefined ? { imageSvg: image.svg } : {}),
          ...(shape.text !== undefined ? { text: shape.text } : {}),
          ...(shape.hoverText !== undefined ? { textHover: shape.hoverText } : {}),
        },
        entity: shape.entity,
      },
    },
  };
}

/**
 * `configurations` v1 (voir BESOINS.md §8/§8.2) : `featureStyle` porte le style par défaut des
 * nouvelles formes dessinées (couleurs par défaut) — gelé tel quel (format historique, voir le
 * commentaire en tête de fichier), contrairement à v2 qui le renomme `defaultShapeStyle` (voir
 * `v2.ts`). Pas d'équivalent hover en v1 : ce champ n'existe pas dans ce format historique.
 */
export interface RawConfigurationsV1 extends RawConfigurations {
  featureStyle?: ShapeStyle;
}

function extractV1DefaultShapeStyles(configurations: RawConfigurationsV1 | undefined): DefaultShapeStyles {
  return { defaultShapeStyle: configurations?.featureStyle };
}

function parseDocument(data: unknown, options: ParseJsonOptions): LoadedData {
  return parseLayeredDocument(
    data as RawDocument<RawFeature, RawConfigurationsV1> | undefined,
    options,
    parseJsonFeature,
    extractV1DefaultShapeStyles,
  );
}

export const v1Connector: JsonFormatConnector = { version: 1, parseDocument };
